import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 文書送付テンプレート API 操作クラス
 */
export default class SendDocumentTemplates {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET SendDocumentTemplate
   */
  public async get(userStore: LoginUser, templateId: number): Promise<SendDocumentTemplatesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      templateId: templateId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocumentTemplate, formatParams);
    const response = await this.commonService.executeGet<SendDocumentTemplatesGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET SendDocumentTemplates
   */
  public async gets(userStore: LoginUser, queryParams?: { startIndex: number; endIndex: number }): Promise<Array<SendDocumentTemplatesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocumentTemplates, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<SendDocumentTemplatesGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      params,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * POST SendDocumentTemplate
   */
  public async post(userStore: LoginUser, requestBody: SendDocumentTemplatesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocumentTemplates, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH SendDocumentTemplates
   */
  public async patch(userStore: LoginUser, templateId: number, requestBody: SendDocumentTemplatesPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      templateId: templateId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocumentTemplate, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE SendDocumentTemplates
   */
  public async delete(userStore: LoginUser, templateId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      templateId: templateId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocumentTemplate, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface SendDocumentTemplatesGetModel {
  /** 施設を特定するためのID */
  facilityId: number;
  /** 一意に識別する ID */
  id: number;

  /** テンプレート種別 */
  templateType: TemplateType;
  /** テンプレート名 */
  name: string;

  /** 文書種類カテゴリID */
  categoryId: number;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string;
  /** 確認しましたボタンを付与するか */
  needConfirm: boolean;
  /** 同意しましたボタンを付与するか */
  needAgreement: boolean;

  /** 記載者名 */
  writtenBy: string;
  /** 記載者ID */
  writerId: string;
  /** 削除フラグ */
  deleteFlag: boolean;
  /** レコード作成日 */
  created: number;
  /** レコード更新日 */
  updated: number;
}

export enum TemplateType {
  /** 家族連絡サービス */
  FAMILY_SERVICE = "familyService",
  /** 入居者募集管理サービス */
  WAITER_SERVICE = "waiterService"
}

export interface SendDocumentTemplatesPostModel {
  /** テンプレート種別 */
  templateType: TemplateType;
  /** テンプレート名 */
  name: string;

  /** 文書種類カテゴリID */
  categoryId: number | null;
  /** タイトル */
  title: string | null;
  /** 本文 */
  text: string | null;
  /** 確認しましたボタンを付与するか */
  needConfirm: boolean;
  /** 同意しましたボタンを付与するか */
  needAgreement: boolean;
}

export interface SendDocumentTemplatesPatchModel {
  /** テンプレート種別 */
  templateType: TemplateType;
  /** テンプレート名 */
  name: string;

  /** 文書種類カテゴリID */
  categoryId: number | null;
  /** タイトル */
  title: string | null;
  /** 本文 */
  text: string | null;
  /** 確認しましたボタンを付与するか */
  needConfirm: boolean;
  /** 同意しましたボタンを付与するか */
  needAgreement: boolean;
  /** 更新日時 */
  updated: number;
}
